import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Plus, X, Save } from 'lucide-react'
import { toast } from 'sonner'

import {
  categories as categoryApi,
  products,
  type AdminCategory,
} from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { FormField } from '../components/FormField'
import { Input, Textarea, Select } from '../components/Input'
import { toFormData } from '../lib/formData'
import { PageLoader } from '../components/Spinner'
import { RichTextEditor } from '../components/RichTextEditor'
import { Dialog, DialogContent, DialogClose } from '../components/Dialog'
import { PhotoGallery, type GalleryPhoto } from '../components/PhotoGallery'

export function ProductEditorPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const isNew = !slug

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)

  // Basic fields
  const [name, setName] = useState('')
  const [productSlug, setProductSlug] = useState('')
  const [slugDirty, setSlugDirty] = useState(false)
  const [categoryId, setCategoryId] = useState('')
  const [sellingPrice, setSellingPrice] = useState('')
  const [originalPrice, setOriginalPrice] = useState('')
  const [description, setDescription] = useState('')
  const [howToUse, setHowToUse] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  /* See BlogPostEditor's note for the cover-image triplet pattern. */
  const [imageFile, setImageFile] = useState<Blob | string | null>(null)
  const [imageCleared, setImageCleared] = useState(false)
  /* Optional extra product-info image shown between description and
     video testimonials on the storefront product page. */
  const [infoImageUrl, setInfoImageUrl] = useState('')
  const [infoImageFile, setInfoImageFile] = useState<Blob | null>(null)
  /* Additional gallery photos (max 4). Server snapshots these via
     the gallery[] key on every save. */
  const [gallery, setGallery] = useState<GalleryPhoto[]>([])
  const [size, setSize] = useState('')
  const [gstRate, setGstRate] = useState('5')

  // Flags
  const [featured, setFeatured] = useState(false)
  const [trending, setTrending] = useState(false)
  const [bestseller, setBestseller] = useState(false)
  const [isNewFlag, setIsNewFlag] = useState(false)
  const [isActive, setIsActive] = useState(true)

  // SEO
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')

  // Arrays
  const [benefits, setBenefits] = useState<string[]>([])
  const [ingredients, setIngredients] = useState<string[]>([])
  const [newBenefit, setNewBenefit] = useState('')
  const [newIngredient, setNewIngredient] = useState('')

  // Categories dropdown — fetched once on mount.
  const [categoryList, setCategoryList] = useState<AdminCategory[]>([])
  const [newCatOpen, setNewCatOpen] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [newCatSlug, setNewCatSlug] = useState('')
  const [newCatSaving, setNewCatSaving] = useState(false)

  useEffect(() => {
    categoryApi
      .list()
      .then((r) => setCategoryList(r.categories))
      .catch(() => {
        // Non-fatal — the editor still works with the slug input as
        // a fallback.
      })
  }, [])

  // Load existing product
  useEffect(() => {
    if (isNew) return
    let cancelled = false
    products
      .detail(slug!)
      .then((r) => {
        if (cancelled) return
        const p = r.product
        setName(p.name)
        setProductSlug(p.slug)
        setSlugDirty(true)
        setCategoryId(p.category.id)
        setSellingPrice(String(p.price / 100))
        setOriginalPrice(String(p.originalPrice / 100))
        setDescription(p.description)
        setHowToUse(p.howToUse)
        setImageUrl(p.image ?? '')
        setInfoImageUrl(p.infoImage ?? '')
        setSize(p.size ?? '')
        setGstRate(String(p.gstRate))
        setFeatured(p.featured)
        setTrending(p.trending)
        setBestseller(p.bestseller)
        setIsNewFlag(p.new)
        setIsActive(p.isActive)
        setMetaTitle(p.metaTitle ?? '')
        setMetaDescription(p.metaDescription ?? '')
        setBenefits(p.benefits)
        setIngredients(p.ingredients)
        // Hydrate gallery rows from the server. Server returns absolute
        // URLs (PUBLIC_BASE_URL prepended). The PhotoGallery keeps
        // server row.id as React key.
        setGallery(
          (p.gallery ?? []).map((g) => ({
            id: `srv-${g.id}`,
            url: g.url ?? '',
            sortOrder: g.sortOrder,
          })),
        )
      })
      .catch((err) => {
        toast.error('Failed to load product', { description: err.message })
        navigate('/products')
      })
      .finally(() => setLoading(false))
    return () => {
      cancelled = true
    }
  }, [slug])

  function autoSlug(s: string) {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 120)
  }

  function addBenefit() {
    const b = newBenefit.trim()
    if (!b) return
    setBenefits((prev) => [...prev, b])
    setNewBenefit('')
  }

  function removeBenefit(i: number) {
    setBenefits((prev) => prev.filter((_, idx) => idx !== i))
  }

  function addIngredient() {
    const ing = newIngredient.trim()
    if (!ing) return
    setIngredients((prev) => [...prev, ing])
    setNewIngredient('')
  }

  function removeIngredient(i: number) {
    setIngredients((prev) => prev.filter((_, idx) => idx !== i))
  }

  async function createCategory() {
    if (!newCatName.trim() || !newCatSlug.trim()) {
      toast.error('Name and slug are required')
      return
    }
    setNewCatSaving(true)
    try {
      const r = await categoryApi.create({
        name: newCatName.trim(),
        slug: newCatSlug.trim(),
      })
      const created = r.category
      setCategoryList((prev) => [...prev, created])
      setCategoryId(created.id)
      setNewCatOpen(false)
      setNewCatName('')
      setNewCatSlug('')
      toast.success('Category created')
    } catch (err) {
      toast.error('Failed to create category', { description: (err as Error).message })
    } finally {
      setNewCatSaving(false)
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)

    const payload: Record<string, unknown> = {
      name,
      slug: productSlug,
      categoryId,
      sellingPrice: Math.round(Number(sellingPrice) * 100),
      originalPrice: Math.round(Number(originalPrice) * 100),
      description,
      howToUse,
      ...(imageFile instanceof Blob
        ? {}
        : imageCleared
          ? { imageUrl: null }
          : { imageUrl: imageUrl || null }),
      ...(infoImageFile instanceof Blob
        ? {}
        : { infoImage: infoImageUrl || null }),
      size: size || null,
      gstRate: Number(gstRate),
      featured,
      trending,
      bestseller,
      isNew: isNewFlag,
      isActive,
      metaTitle: metaTitle || null,
      metaDescription: metaDescription || null,
      // Gallery entries: rows with a freshly-picked file get an empty
      // `url` placeholder (the server fills it from the next
      // `gallery_file` field). URL-only rows pass through. Rows
      // with no URL AND no file are dropped (no dangling empty row).
      gallery: gallery
        .filter((g) => g.url.trim().length > 0 || g.file)
        .map((g, i) => ({
          url: g.file ? '' : g.url.trim(),
          sortOrder: i,
        })),
      benefits,
      ingredients,
    }

    // Trigger the multipart path whenever any photo (cover or any
    // gallery row) is freshly picked. URL-only edits stay on the
    // JSON path.
    const hasCoverFile = imageFile instanceof Blob
    const hasInfoImageFile = infoImageFile instanceof Blob
    const hasGalleryFiles = gallery.some((g) => g.file)

    try {
      if (hasCoverFile || hasInfoImageFile || hasGalleryFiles) {
        const fileMap: Record<string, Blob | File | Blob[] | File[]> = {}
        if (hasCoverFile) {
          fileMap.image = imageFile as Blob
        }
        if (hasInfoImageFile) {
          fileMap.infoImage = infoImageFile as Blob
        }
        const galleryBlobs = gallery
          .filter((g): g is GalleryPhoto & { file: Blob } => g.file != null)
          .map((g) => g.file)
        if (galleryBlobs.length > 0) {
          fileMap.gallery_file = galleryBlobs
        }
        const fd = toFormData(payload, fileMap)
        if (isNew) {
          await products.create(fd)
          toast.success('Product created')
        } else {
          await products.update(slug!, fd)
          toast.success('Product updated')
        }
      } else {
        if (isNew) {
          await products.create(payload)
          toast.success('Product created')
        } else {
          await products.update(slug!, payload)
          toast.success('Product updated')
        }
      }
      navigate('/products')
    } catch (err) {
      toast.error(isNew ? 'Failed to create product' : 'Failed to update product', {
        description: (err as Error).message,
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageLoader />

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-6xl pb-24">
      <Link
        to="/products"
        className="text-sm text-slatey hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="size-3.5" /> Back to products
      </Link>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold text-ink">
          {isNew ? 'New product' : 'Edit product'}
        </h1>
        <Button onClick={onSubmit} loading={saving}>
          <Save className="size-4" />
          {isNew ? 'Create product' : 'Save changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5">
        {/* ─── LEFT: main content ─────────────────────────────── */}
        <div className="space-y-5 min-w-0">
          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Basic information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Product name" required>
                <Input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (isNew && !slugDirty && !productSlug) {
                      setProductSlug(autoSlug(e.target.value))
                    }
                  }}
                  placeholder="Himalayan Shilajit Premium"
                  required
                />
              </FormField>
              <FormField
                label="Slug"
                required
                hint="URL identifier — auto-generated from name if blank"
              >
                <Input
                  value={productSlug}
                  onChange={(e) => {
                    setProductSlug(e.target.value)
                    setSlugDirty(true)
                  }}
                  placeholder="himalayan-shilajit-premium"
                  required
                />
              </FormField>
            </div>
            <FormField label="Category" required hint="Pick the product category. Create new ones inline.">
              <div className="flex gap-2">
                <Select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="flex-1"
                  required
                >
                  <option value="">— Select a category —</option>
                  {categoryList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.productCount > 0 ? ` · ${c.productCount} product${c.productCount === 1 ? '' : 's'}` : ''}
                    </option>
                  ))}
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={() => setNewCatOpen(true)}>
                  <Plus className="size-4" />
                  New
                </Button>
              </div>
            </FormField>
          </Card>

          <Card className="p-5">
            <PhotoGallery
              cover={imageUrl || null}
              coverFile={imageFile}
              onCoverChange={(next) => {
                if (next instanceof Blob) {
                  setImageFile(next)
                  setImageCleared(false)
                  setImageUrl('') // clear stale URL while fresh blob is pending
                } else if (typeof next === 'string') {
                  setImageUrl(next)
                  setImageFile(null)
                  setImageCleared(false)
                } else {
                  setImageUrl('')
                  setImageFile(null)
                  setImageCleared(true)
                }
              }}
              gallery={gallery}
              onGalleryChange={setGallery}
            />
          </Card>

          <Card className="p-5 space-y-3">
            <FormField label="Product info image" hint="Optional secondary image shown between description and video testimonials on the storefront. Upload or paste a URL.">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Input
                    value={infoImageUrl}
                    onChange={(e) => { setInfoImageUrl(e.target.value); setInfoImageFile(null) }}
                    placeholder="https://… or leave empty"
                  />
                  <input
                    type="file"
                    accept="image/webp,image/png,image/jpeg,image/avif"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) { setInfoImageFile(f); setInfoImageUrl('') }
                    }}
                    className="mt-2 text-xs text-slatey file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-line file:text-xs file:font-medium file:bg-white file:text-ink hover:file:bg-ivory file:cursor-pointer"
                  />
                </div>
                {(infoImageUrl || infoImageFile) ? (
                  <img
                    src={infoImageFile ? URL.createObjectURL(infoImageFile) : infoImageUrl}
                    alt="Info preview"
                    className="size-20 rounded-md object-cover bg-ivory border border-line shrink-0"
                  />
                ) : null}
              </div>
            </FormField>
          </Card>

          <Card className="p-5 space-y-3">
            <FormField label="Description" required hint="Rich text — toolbar supports formatting, links, and lists.">
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Write a product description…"
                minHeight={320}
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-3">
            <FormField label="How to use" required hint="Plain text. Short instructions for the customer.">
              <Textarea
                value={howToUse}
                onChange={(e) => setHowToUse(e.target.value)}
                placeholder="Take one capsule daily with warm water…"
                rows={4}
                required
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-3">
            <h2 className="font-display text-base font-semibold text-ink">Benefits</h2>
            <div className="flex flex-wrap gap-2 mb-3">
              {benefits.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs bg-forest/10 text-forest px-2 py-1 rounded-full">
                  {b}
                  <button type="button" onClick={() => removeBenefit(i)} className="hover:text-danger">
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                placeholder="Add a benefit..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addBenefit()
                  }
                }}
              />
              <Button type="button" variant="outline" size="sm" onClick={addBenefit}>
                <Plus className="size-3.5" /> Add
              </Button>
            </div>
          </Card>

          <Card className="p-5 space-y-3">
            <h2 className="font-display text-base font-semibold text-ink">Ingredients</h2>
            <div className="flex flex-wrap gap-2 mb-3">
              {ingredients.map((ing, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs bg-saffron/20 text-amber-dark px-2 py-1 rounded-full">
                  {ing}
                  <button type="button" onClick={() => removeIngredient(i)} className="hover:text-danger">
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newIngredient}
                onChange={(e) => setNewIngredient(e.target.value)}
                placeholder="Add an ingredient..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addIngredient()
                  }
                }}
              />
              <Button type="button" variant="outline" size="sm" onClick={addIngredient}>
                <Plus className="size-3.5" /> Add
              </Button>
            </div>
          </Card>
        </div>

        {/* ─── RIGHT: sticky sidebar ─────────────────────────── */}
        <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Pricing</h2>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Selling (₹)" required>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Original (₹)" required>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={originalPrice}
                  onChange={(e) => setOriginalPrice(e.target.value)}
                  required
                />
              </FormField>
            </div>
            <FormField label="GST rate">
              <Select value={gstRate} onChange={(e) => setGstRate(e.target.value)}>
                <option value="0">0%</option>
                <option value="5">5%</option>
                <option value="12">12%</option>
                <option value="18">18%</option>
                <option value="28">28%</option>
              </Select>
            </FormField>
            <FormField label="Size (optional)">
              <Input
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder="500g"
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Visibility & badges</h2>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="size-4 mt-0.5 accent-forest"
              />
              <div>
                <p className="text-sm font-medium text-ink">Active</p>
                <p className="text-xs text-slatey">Visible on the storefront when checked.</p>
              </div>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {([
                { label: 'Featured', val: featured, set: setFeatured },
                { label: 'Trending', val: trending, set: setTrending },
                { label: 'Bestseller', val: bestseller, set: setBestseller },
                { label: 'New', val: isNewFlag, set: setIsNewFlag },
              ] as const).map(({ label, val, set }) => (
                <label
                  key={label}
                  className="flex items-center gap-2 cursor-pointer p-2 rounded-md border border-line hover:bg-ivory transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={val}
                    onChange={(e) => set(e.target.checked)}
                    className="size-4 accent-forest rounded"
                  />
                  <span className="text-sm text-ink">{label}</span>
                </label>
              ))}
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">SEO</h2>
            <FormField
              label="Meta title"
              hint={`Defaults to product name when blank. Up to 255 chars.`}
            >
              <Input
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                maxLength={255}
                placeholder={name.slice(0, 60) || 'Defaults to product name'}
              />
            </FormField>
            <FormField
              label="Meta description"
              hint="Defaults to description excerpt. Up to 500 chars."
            >
              <Textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                maxLength={500}
                className="min-h-[80px]"
                placeholder={description.replace(/<[^>]+>/g, ' ').slice(0, 160) || 'Defaults to description'}
              />
            </FormField>
          </Card>
        </aside>
      </div>

      {/* Sticky bottom submit bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-white/90 backdrop-blur px-6 py-3 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/products')}>
          Cancel
        </Button>
        <Button onClick={onSubmit} loading={saving}>
          <Save className="size-4" />
          {isNew ? 'Create product' : 'Save changes'}
        </Button>
      </div>

      {/* Inline "new category" dialog */}
      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent title="New category" description="Quickly add a product category.">
          <div className="space-y-4">
            <FormField label="Name" required>
              <Input
                value={newCatName}
                onChange={(e) => {
                  setNewCatName(e.target.value)
                  if (!newCatSlug) setNewCatSlug(autoSlug(e.target.value))
                }}
                required
              />
            </FormField>
            <FormField label="Slug" required hint="Lowercase letters, digits, hyphens.">
              <Input
                value={newCatSlug}
                onChange={(e) => setNewCatSlug(e.target.value)}
                placeholder="category-slug"
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={createCategory} loading={newCatSaving}>
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  )
}